import type { LeadInput } from '@jlg-website/shared';

/** The two fields of a lead payload that describe the visitor's analytics session. */
export type LeadSession = Pick<LeadInput, 'session' | 'analytics'>;

/**
 * Reads the analytics session for a `/api/v1/lead` payload — the one place that decides what a
 * lead carries when there is no session to report.
 *
 * `window.JLG.analytics` is filled asynchronously by the external analytics script and never
 * arrives at all when an ad blocker stops it, so a visitor can submit before there is a session.
 * `leadInput` requires both fields, so reading the globals inline used to send `undefined` for
 * each and lose the lead. A lead is worth more than its attribution: the payload degrades to an
 * empty session id and a null ip/user agent — the shape `SessionContent` and the lead exporter
 * already read defensively — and keeps the url so the submission is still traceable to a page.
 */
export function readLeadSession(): LeadSession {
  if (typeof window === 'undefined') {
    return { session: '', analytics: { ip_address: null, user_agent: null, url: null } };
  }

  const session = window.JLG?.analytics?.session;

  if (!session) {
    return {
      session: '',
      analytics: { ip_address: null, user_agent: null, url: window.location.href },
    };
  }

  return { session: session.session_id, analytics: session };
}
